
import React, { useState, useEffect, useMemo } from "react";
import {RootState} from "../../app/store";
import {
    Box,
    Typography,
    Toolbar,
    InputAdornment,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableSortLabel,
    TableContainer,
    TableHead,
    TableFooter,
    TableRow,
    TablePagination,
    TextField,
    Button,
    LinearProgress
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses';
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuIcon from "@mui/icons-material/Menu";
import MonitorIcon from '@mui/icons-material/Monitor';
import AccountCircle from "@mui/icons-material/AccountCircle";
import {useTheme} from '@mui/material/styles';
import {LayoutRelatedListColumnModel,
        LayoutRelatedListButtonModel,
        LayoutRelatedListModel,
        ActionModel,
        RecordModel,
        ObjectModel} from '../../models/models';
import {SortDirectionType,
        SortOrderModel,
        SortOrderFactory} from '../../models/modelsUserConfiguration';
import {PaginatorModel,
        PaginatorFactory,
        SearchTermModel,
        SearchTermFactory,
        RelatedListConfigurationModel,
        RelatedListConfigurationFactory} from '../../models/modelsListConfiguration';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {SearchFieldComponent} from "../../components/searchField/SearchField";
import { ActionComponent } from "../../components/actions-r/Action";
import {CustomIconComponent} from "../../components/icons/CustomIcon";
import { RecordCreateDialogComponent } from "../recordCreateDialog/RecordCreateDialog";
import {useDispatch, useSelector} from "react-redux";
import {getAppName, getObjectName, getRecordUuid } from "../../utils/helperRouter";
import {Link, useParams} from 'react-router-dom';
import { formatDate, formatDatetime } from "../../utils/dateFunctions";
import {retrieveRelatedRecords,
        prefetchRelatedRecords,
        storePaginator,
        storeSortOrders,
        storeSearchTerm,
        purgePaginator,
        purgeSearchTerm,
        purgeRecords,
        selectListState,
        selectRecords} from "../../screens/object/relatedRecordsSlice";
import { useAuth } from "../../hooks/useAuth";
import debounce from "lodash.debounce";
import {ObjectAvatar} from '../icons/ObjectAvatar';

export const RelatedListComponent: React.FC<{
  layoutRelatedList: LayoutRelatedListModel,
  columns: LayoutRelatedListColumnModel[],
  selectRecord: (recordUuid: string, referencedObject: ObjectModel) => void
}> = ({
  layoutRelatedList,
  columns,
  selectRecord
}) => {

  const dispatch = useDispatch<any>();

  const columnAlignment = 'left'

  const styles = ContainerClasses();
  const theme = useTheme();

  const [selectedAction, setSelectedAction] = useState<ActionModel | null>(null);

  let {appName, objectName, recordUuid} = useParams();
  const {accessToken, onInvalidToken} = useAuth();


  const listState = useSelector((state: RootState) => selectListState(state,layoutRelatedList));
  const records = useSelector((state: RootState) => selectRecords(state,layoutRelatedList));

  const isFetching = listState.isFetching;
  const listConfig = listState.listConfig
  const [searchText, setSearchText] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const searchRecordsDebounced = useMemo(
    () =>
    debounce(async (search: string) => {
      try {
        const thisSearchText = search.toLowerCase()
        const thisSearchTerm = new SearchTermModel(thisSearchText,false);

        await dispatch(purgeRecords(layoutRelatedList.Uuid_sys))
        await dispatch(purgePaginator(layoutRelatedList.Uuid_sys))
        const newListConfig = {...listConfig,
                               paginator: new PaginatorFactory().init(),
                               searchTerm: thisSearchTerm}
        const thisObjectName = getObjectName(objectName)
        const thisRecordUuid = getRecordUuid(recordUuid)
        await dispatch(retrieveRelatedRecords(thisObjectName, thisRecordUuid, layoutRelatedList, newListConfig, accessToken, false, onInvalidToken));
      } finally {
        setIsSearching(false);
      }
    }, 1000),
    []
  );

  //cleanup
  useEffect(() => {
    return () => {
      searchRecordsDebounced.cancel();
    }
  }, [searchRecordsDebounced]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleChangeRowsPerPage = (event: any) => {
    /*
    setRowsPerPage(event.target.value);
    setPaginationPage(0);
    */
    return
  }


  const reloadRecords = (layoutRelatedList: LayoutRelatedListModel) => {
    const thisObjectName = getObjectName(objectName)
    const thisRecordUuid = getRecordUuid(recordUuid)
    const thisListConfig = null;
    dispatch(purgeRecords(layoutRelatedList.Uuid_sys))
    dispatch(purgePaginator(layoutRelatedList.Uuid_sys))
    dispatch(retrieveRelatedRecords(thisObjectName, thisRecordUuid, layoutRelatedList, thisListConfig, accessToken, false, onInvalidToken));
  }


  const handleChangePage = (event: any, newPage: number) => {
    //set the new paginator when switching a page

    const pageCount = Math.ceil(listConfig.paginator.recordCount/listConfig.paginator.pageSize)
    const nextPage = Math.min(newPage + 1, pageCount-1); //use pageCount-1 because pageNumber is actually the index
    const previousPage = Math.max(0,newPage - 1);

    const newListConfig = {...listConfig,
                           paginator: {...listConfig.paginator,
                                       currentPage: newPage,
                                       previousPage: previousPage,
                                       nextPage: nextPage}
                          };

    //update the paginator
    dispatch(storePaginator({layoutRelatedListUuid: layoutRelatedList.Uuid_sys,
                             paginator: newListConfig.paginator}));

    //prefetch records
    const thisObjectName = getObjectName(objectName)
    const thisRecordUuid = getRecordUuid(recordUuid)
    dispatch(prefetchRelatedRecords(thisObjectName,
                             thisRecordUuid,
                             layoutRelatedList,
                             newListConfig,
                             accessToken));
  }



  const handleSort = (event: any, column: LayoutRelatedListColumnModel) => {
    const columnUuid = column.Uuid_sys;
    const fieldApiName = column.ReferencedField_sys.ApiName_sys;

    const oldSortDirection = getSortDirection();
    const sortedByColumnUuid = getSortedBy()

    let newSortDirection = 'asc' as SortDirectionType;

    const newSortDirectionMap: Record<SortDirectionType,SortDirectionType> = {
      asc: 'desc',
      desc: 'asc'
    }

    if(columnUuid === sortedByColumnUuid){
      //change sort direction
      newSortDirection = newSortDirectionMap[oldSortDirection]
    }

    const thisSortOrder = new SortOrderModel(1,newSortDirection,columnUuid,fieldApiName)

    if(listConfig.paginator.pageSize*listConfig.paginator.loadedPages.length >= listConfig.paginator.recordCount){
      //all pages already fetched, perform only a frontend sort
      dispatch(storeSortOrders({layoutRelatedListUuid: layoutRelatedList.Uuid_sys,
                                sortOrders: [thisSortOrder]}))
    } else {
      dispatch(storeSortOrders({layoutRelatedListUuid: layoutRelatedList.Uuid_sys,
                                sortOrders: [thisSortOrder]}))
      dispatch(purgeRecords(layoutRelatedList.Uuid_sys))
      dispatch(purgePaginator(layoutRelatedList.Uuid_sys))
      const newListConfig = {...listConfig,
                              paginator: new PaginatorFactory().init(),
                              searchTerm: new SearchTermFactory().init(),
                              sortOrders: [thisSortOrder]}
      const thisObjectName = getObjectName(objectName)
      const thisRecordUuid = getRecordUuid(recordUuid)
      dispatch(retrieveRelatedRecords(thisObjectName, thisRecordUuid, layoutRelatedList, newListConfig, accessToken, false, onInvalidToken));
    }
  }

  const searchRecords = (searchText: string) => {
    setSearchText(searchText);
    setIsSearching(true);
    searchRecordsDebounced(searchText);
  }

  const clearSearch = () => {
    searchRecords("")
  }

  const getSortedBy = () => {
    if(listConfig.sortOrders.length > 0){
      return listConfig.sortOrders[0].sortColumnUuid
    }
    return ''
  }
  const getSortDirection = () => {
    if(listConfig.sortOrders.length > 0){
      return listConfig.sortOrders[0].sortDirection
    }
    return 'asc' as SortDirectionType
  }

  const lookupObjectName = layoutRelatedList.ReferencedObject_sys.ApiName_sys;
  const appApiName = getAppName(appName)

  const rowElement = (record: RecordModel) => {

    const recordFields = record.fields;
    const recordUuidField = recordFields["Uuid_sys"]

    var linkSet = false;

    const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>)=>{
      //allow selecting of text within row
      const selectedText = window.getSelection()?.toString();
      if(selectedText && selectedText.length > 0) {
        event.stopPropagation();
        return;
      }
      selectRecord(recordUuidField?.fieldValue, layoutRelatedList.ReferencedObject_sys)
    }

    const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      //stop propagation when clicking a link
      event.stopPropagation();
    }

    return (
      <TableRow
         key={recordUuidField?.fieldValue}
         hover
         onClick={handleRowClick}
      >
         {columns.map((thisColumn) => {
           const columnFieldName = thisColumn.ReferencedField_sys.ApiName_sys
           const currentField = recordFields[columnFieldName]

           if(currentField === null || currentField === undefined){
             return (
                  <TableCell key={`${recordUuidField?.fieldValue} - ${thisColumn.Uuid_sys}`} scope="row" align={columnAlignment}>
                     undefined
                  </TableCell>
                )
           }

           const currentFieldType = currentField.fieldType

           if(currentFieldType === 'ForeignKey'){
             if(currentField.fieldValue){
               const lookup_object_name = currentField.fieldValue.lookup_object;
               const lookup_uuid = currentField.fieldValue.Uuid_sys;
               const appApiName = getAppName(appName)
               const link_to = '/' + appApiName + '/' + lookup_object_name + '/' + lookup_uuid;
               return (
                 <TableCell key={`${recordUuidField?.fieldValue} - ${thisColumn.Uuid_sys}`} scope="row" align={columnAlignment}>
                   <Link to={link_to} onClick={handleLinkClick}>
                     {currentField.fieldValue.lookup_display_value}
                   </Link>
                 </TableCell>
               )
             }
             return (
                  <TableCell key={`${recordUuidField?.fieldValue} - ${thisColumn.Uuid_sys}`} scope="row" align={columnAlignment}>
                  </TableCell>
                )
           }

           if(currentFieldType === 'OneToOneField'){
             if(currentField.fieldValue){
               const lookup_object_name = currentField.fieldValue.lookup_object;
               const lookup_uuid = currentField.fieldValue.Uuid_sys;
               const appApiName = getAppName(appName)
               const link_to = '/' + appApiName + '/' + lookup_object_name + '/' + lookup_uuid;
               return (
                 <TableCell key={`${recordUuidField?.fieldValue} - ${thisColumn.Uuid_sys}`} scope="row" align={columnAlignment}>
                   <Link to={link_to} onClick={handleLinkClick}>
                     {currentField.fieldValue.lookup_display_value}
                   </Link>
                 </TableCell>
               )
             }
             return (
                  <TableCell key={`${recordUuidField?.fieldValue} - ${thisColumn.Uuid_sys}`} scope="row" align={columnAlignment}>
                  </TableCell>
                )
           }

           if(currentFieldType === 'DateTimeField'){
             const thisValue = formatDatetime(new Date(currentField?.fieldValue), {variant: 'D'})
             return(
               <TableCell key={`${recordUuidField?.fieldValue} - ${thisColumn.Uuid_sys}`} scope="row" align={columnAlignment}>
                 {thisValue}
               </TableCell>
             )
           }


           if(currentFieldType === 'BooleanField'){
             return(
               <TableCell key={`${recordUuidField?.fieldValue} - ${thisColumn.Uuid_sys}`} scope="row" align={columnAlignment}>
                 {currentField.fieldValue === true  &&
                   <CheckCircleIcon sx={{color: theme.extras.boolTrue}}/>
                 }
                 {/*
                   <CancelIcon sx={{color: theme.extras.boolFalse}}/>
                 */}
               </TableCell>
             )
           }

           if(currentFieldType === 'FileField'){
             return(
               <TableCell key={`${recordUuidField?.fieldValue} - ${thisColumn.Uuid_sys}`} scope="row" align={columnAlignment}>
                 {currentField.fieldValue?.filename}
               </TableCell>
             )
           }

           if(linkSet === false){
             //default
             linkSet=true;
             const link_to = `/${appApiName}/${lookupObjectName}/${recordUuidField?.fieldValue}`;

             return (
               <TableCell key={`${recordUuidField?.fieldValue} - ${thisColumn.Uuid_sys}`} scope="row" align={columnAlignment}>
                 <Link to={link_to} onClick={handleLinkClick}>
                   {currentField?.fieldValue}
                 </Link>
               </TableCell>
             )
           }

           //default
           return (
             <TableCell key={`${recordUuidField?.fieldValue} - ${thisColumn.Uuid_sys}`} scope="row" align={columnAlignment}>
                 {currentField?.fieldValue}
             </TableCell>
           )
         })}

      </TableRow>
    )
  }


  const buttonElement = (button: LayoutRelatedListButtonModel) => {

    return (
      <Button
        key={button.Uuid_sys}
        color="primary"
        variant="contained"
        onClick={() => {setSelectedAction(button.ReferencedAction_sys)}}
      >
        {button.ReferencedAction_sys.Label_sys}
      </Button>
    )
  }

  return (

    <Box sx={{p: 2}}>

      {isFetching || isSearching && <LinearProgress color="primary" />}


    {
      selectedAction?.ApiName_sys === 'Create_c' &&
      <RecordCreateDialogComponent
        action = {selectedAction}
        onClose = {() => setSelectedAction(null)}
        fromRelatedList = {layoutRelatedList}
      />
    }

       <Box sx={styles.containerRowB}>
          <Box>
             <ObjectAvatar
               objectMetadata={layoutRelatedList.ReferencedObject_sys}
               size={36}
             />
          </Box>
       </Box>

       <Box sx={styles.containerRowB}>
          <Box>
            <Typography>
              {layoutRelatedList.Label_sys}
            </Typography>
            </Box>
      </Box>


       <Box
         sx={{
           flex: 1,
           display: "flex",
           flexDirection: "row",
           justifyContent: "flex-end",
           alignItems: "center",
           gap: 2,
           pb: 2
         }}
        >
          {layoutRelatedList.buttons.map((button) => {
            return buttonElement(button);
          })}

          <Button
            color="primary"
            variant="contained"
            onClick={() => {reloadRecords(layoutRelatedList)}}
          >
            Reload Records
          </Button>

        </Box>

       <Box sx={{flex: 1,
                 display: "flex",
                 flexDirection: "row",
                 justifyContent: "flex-end",
                 alignItems: "center"}}
        >
           <SearchFieldComponent
              searchText={searchText}
              setSearchText={searchRecords}
              clearSearchText={clearSearch}
           />
        </Box>


        <TableContainer sx={{maxHeight: 650, width: '100%'}}>

           <Table stickyHeader sx={{minWidth: 650}}>
              <TableHead>
                 <TableRow>
                    {columns.map((column) => {
                           const thisIcon = column.ReferencedField_sys.ReferencedObject_sys?.ReferencedIcon_sys;

                           return (
                                <TableCell
                                    key={column.Uuid_sys}
                                    align={columnAlignment}
                                >
                                    <TableSortLabel
                                       active={getSortedBy() === column.Uuid_sys}
                                       direction={getSortDirection()}
                                       onClick={(event) => handleSort(event, column)}
                                       >
                                    <Typography style={{fontWeight: 'bold'}}>
                                       {column.ReferencedField_sys.Label_sys}
                                    </Typography>
                                    {thisIcon &&
                                      <CustomIconComponent
                                        icon={thisIcon}
                                        sx={{
                                          marginLeft: '0.2rem'
                                        }}
                                      />
                                    }
                                    </TableSortLabel>
                                </TableCell>
                              )
                            })}
                  </TableRow>
              </TableHead>

              <TableBody>
                   {records.slice(listConfig.paginator.currentPage*listConfig.paginator.pageSize, listConfig.paginator.currentPage*listConfig.paginator.pageSize + listConfig.paginator.pageSize)
                                  .map((record) => rowElement(record))}
              </TableBody>


              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[listConfig.paginator.pageSize]}
                    count={listConfig.paginator.recordCount}
                    rowsPerPage={listConfig.paginator.pageSize}
                    page={listConfig.paginator.currentPage}
                    onPageChange={(event, newPage) => handleChangePage(event, newPage)}
                    onRowsPerPageChange={(event) => handleChangeRowsPerPage(event)}
                  />
                </TableRow>
              </TableFooter>
            </Table>

        </TableContainer>

    </Box>
  );

};
